import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getYardsByCompanyId, getShippingLinesByYards } from "../../pages/Home/api";
import { getScansByYardsAndDate, getScansByYardsAndDays } from "../../pages/Report/api";
import { formatDateToISOString } from "../../utils/utils";

const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 6);
  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(today),
  };
};

// A persisted endDate older than "today" is stale and must not be trusted.
const isDateStale = (date) => !!date && date < getLocalDateString();

export const fetchReportData = createAsyncThunk(
  "report/fetchReportData",
  async ({ companyId, filters }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId);
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);

      const shippingRes = await getShippingLinesByYards(allYardIds);
      const allShippingLines = shippingRes.status ? shippingRes.data : [];
      const allShippingLineIds = allShippingLines.map((sl) => sl.shipping_line_id);
      const shippingLineOptions = allShippingLines.map((sl) => ({
        label: sl.shipping_line_name,
        value: sl.shipping_line_id,
        yard_id: sl.yard_id,
      }));

      const yardsToUse = filters.yards?.length > 0 ? filters.yards : allYardIds;
      const shippingLinesToUse = filters.useShippingLineFilter ? filters.shippingLines : [];
      const defaultDates = getDefaultDates();

      const payload = { yard_ids: yardsToUse };
      if (filters.useShippingLineFilter && shippingLinesToUse.length > 0)
        payload.shipping_line_ids = shippingLinesToUse;

      let scansData;
      let lastFetchedFilters;

      if (filters.filterMode === "days" && filters.days) {
        payload.days = Number(filters.days);
        const response = await getScansByYardsAndDays(payload);
        scansData = response.data;
        lastFetchedFilters = {
          yards: yardsToUse,
          filterMode: "days",
          days: filters.days,
          shippingLines: shippingLinesToUse,
          useShippingLineFilter: filters.useShippingLineFilter || false,
        };
      } else {
        // Never trust a persisted endDate that is older than today — fall
        // back to defaultDates.endDate (today) in that case. This guard is
        // applied here, at the point the payload is built, so even the very
        // first request on page load is correct — not just later refetches.
        const safeStartDate = filters.startDate || defaultDates.startDate;
        const safeEndDate = isDateStale(filters.endDate)
          ? defaultDates.endDate
          : filters.endDate || defaultDates.endDate;

        payload.start_date = formatDateToISOString(safeStartDate, true);
        payload.end_date = formatDateToISOString(safeEndDate, false);
        const response = await getScansByYardsAndDate(payload);
        scansData = response.data;
        lastFetchedFilters = {
          yards: yardsToUse,
          filterMode: "date",
          startDate: payload.start_date,
          endDate: payload.end_date,
          shippingLines: shippingLinesToUse,
          useShippingLineFilter: filters.useShippingLineFilter || false,
        };
      }

      return {
        yardOptions,
        allYardIds,
        allShippingLines,
        allShippingLineIds,
        shippingLineOptions,
        scansData,
        defaultDates,
        lastFetchedFilters,
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchScans = createAsyncThunk(
  "report/fetchScans",
  async (payload, { rejectWithValue }) => {
    try {
      const res = payload.days
        ? await getScansByYardsAndDays(payload)
        : await getScansByYardsAndDate(payload);

      const lastFetchedFilters = payload.days
        ? {
            yards: payload.yard_ids,
            filterMode: "days",
            days: String(payload.days),
            shippingLines: payload.shipping_line_ids || [],
            useShippingLineFilter: !!(payload.shipping_line_ids?.length),
          }
        : {
            yards: payload.yard_ids,
            filterMode: "date",
            startDate: payload.start_date,
            endDate: payload.end_date,
            shippingLines: payload.shipping_line_ids || [],
            useShippingLineFilter: !!(payload.shipping_line_ids?.length),
          };

      return { scansData: res.data, lastFetchedFilters };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const reportSlice = createSlice({
  name: "report",
  initialState: {
    scansData: [],
    allShippingLines: [],
    shippingLineOptions: [],
    yardOptions: [],
    isLoading: false,
    error: null,
    lastFetchedFilters: null,
  },
  reducers: {
    setShippingLineOptions: (state, action) => {
      state.shippingLineOptions = action.payload;
    },
    removeScan: (state, action) => {
      state.scansData = state.scansData.filter(
        (scan) => scan.scan_id !== action.payload
      );
    },
    updateScanStageStatus: (state, action) => {
      const { scanId, stageName, reported_to_manager, report_published } = action.payload;
      const scan = state.scansData.find((s) => String(s.scan_id) === String(scanId));
      if (scan) {
        const stage = scan.scan_stages?.find((s) => s.stage_name === stageName);
        if (stage) {
          if (reported_to_manager !== undefined) stage.reported_to_manager = reported_to_manager;
          if (report_published !== undefined) stage.report_published = report_published;
        }
      }
    },
    updateScanContainerNumber: (state, action) => {
      const { scanId, container_number } = action.payload;
      const scan = state.scansData.find((s) => String(s.scan_id) === String(scanId));
      if (scan) {
        scan.container_number = container_number;
      }
    },
    resetReportData: (state) => {
      state.scansData = [];
      state.lastFetchedFilters = null;
    },
    resetReportYards: (state) => {
      state.yardOptions = [];
      state.scansData = [];
      state.allShippingLines = [];
      state.shippingLineOptions = [];
      state.lastFetchedFilters = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReportData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.yardOptions = action.payload.yardOptions;
        state.allShippingLines = action.payload.allShippingLines;
        state.shippingLineOptions = action.payload.shippingLineOptions;
        state.scansData = action.payload.scansData;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchReportData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchScans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchScans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scansData = action.payload.scansData;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchScans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setShippingLineOptions,
  removeScan,
  updateScanStageStatus,
  updateScanContainerNumber,
  resetReportData,
  resetReportYards,
} = reportSlice.actions;

export default reportSlice.reducer;